
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { submitToZoho } from "@/utils/secureFormSubmission";

const HeroSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    console.log("Demo request submitted:", formData);
    
    try {
      const success = await submitToZoho({
        ...formData,
        formType: "Demo Request - Products"
      });
      
      if (success) {
        toast.success("Demo request submitted! Our team will contact you shortly.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          message: ""
        });
      } else {
        toast.error("There was an error submitting the form. Please try again.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-gradient-to-b from-slate-50 to-white py-8 md:py-12 lg:py-16 xl:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-3 leading-tight">
              Spharaka Networks: Industry's First True XDR Platform Powered By <span className="text-[#8932c8]">Agentic AI</span>
            </h1>
            <div className="space-y-4 mb-4 md:mb-6 lg:mb-8">
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
                Spharaka Networks Autonomous Security XDR platform that transforms cybersecurity with accurate threat detection, autonomous investigation and remediation.
              </p>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
                Real-time threat hunting, autonomous incident response, and continuous security monitoring for enterprises, SMBs, and government organizations.
              </p>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="bg-[#8932c8] hover:bg-[#7a2cb3] w-full sm:w-auto px-4 sm:px-6 md:px-8 py-3 md:py-4 lg:py-6 text-sm sm:text-base md:text-lg">
                  Request a Demo
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] w-[95vw] max-w-[95vw] sm:w-auto mx-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-xl md:text-2xl font-bold">Request a Demo</DialogTitle>
                  <DialogDescription className="text-sm sm:text-base">
                    Fill out the form below and our team will contact you to schedule a personalized demo of Spharaka XDR.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                      <Input 
                        id="firstName" 
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John" 
                        required
                        disabled={isSubmitting}
                        className="text-base"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                      <Input 
                        id="lastName" 
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe" 
                        required
                        disabled={isSubmitting}
                        className="text-base"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1">Work Email</label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.doe@company.com" 
                      required
                      disabled={isSubmitting}
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-1">Company</label>
                    <Input 
                      id="company" 
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Acme Inc." 
                      required
                      disabled={isSubmitting}
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1">How can we help?</label>
                    <Textarea 
                      id="message" 
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your security challenges..." 
                      disabled={isSubmitting}
                      className="text-base min-h-[80px]"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#8932c8] hover:bg-[#7a2cb3] py-3"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-4">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="hidden md:block">
            <img
              src="/lovable-uploads/a8880a54-7ffe-40e6-839e-5c84c19f80fe.png"
              alt="asset visualization capability of Spharaka XDR - Autonomous security platform with AuraXP"
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </div>
          <div className="block md:hidden mt-4">
            <img
              src="/lovable-uploads/a8880a54-7ffe-40e6-839e-5c84c19f80fe.png"
              alt="asset visualization capability of Spharaka XDR - Autonomous security platform with AuraXP"
              className="w-full h-auto rounded-lg shadow-md"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
